import React, { useState, useCallback } from "react";
import { View, Text, Image } from "react-native";
import { CommonButton } from "../../../../../common/CommonButton";
import { useWsProvider } from "../../../../../context/NativeApplicationContext";
import { AppColors } from "../../../../../utility/AppColors";
import { AppImages } from "../../../../../utility/AppImages";

const COLORS = {
  green: "#4ADE80",
  purple: "#6D08C3",
  purpleThumb: "#6D08C3",
  yellow: "#FDE047",
  darkGreen: "#14532D",
  blue: "#284CB8",
  liveRed: "#DC2626",
  redBorder: "#C3081B",
  obsBg: "#14532D",
  obsText: "#16A34A",
  liveBg: "#7F1D1D",
  liveText: "#FFF",
};

const getStatusDisplayText = (state?: string): string => {
  switch (state) {
    case "service-starting":
      return "Service Starting";
    case "idle":
      return "Ready";
    case "streaming-starting":
      return "Starting Stream";
    case "streaming":
      return "Streaming Live";
    case "network-disconnected":
      return "Network Disconnected";
    case "network-reconnecting":
      return "Reconnecting Network";
    case "restarting-streaming":
      return "Restarting Stream";
    case "service-error":
      return "Service Error";
    case "service-restarting":
      return "Restarting Service";
    case "streaming-error":
      return "Streaming Error";
    default:
      return state || "Ready";
  }
};

const getStatusColor = (state?: string): string => {
  switch (state) {
    case "idle":
      return "#22C55E"; // Green - ready
    case "streaming":
      return "#22C55E"; // Green - actively streaming
    case "service-starting":
    case "streaming-starting":
    case "network-reconnecting":
    case "restarting-streaming":
    case "service-restarting":
      return "#F59E0B"; // Yellow - in progress
    case "network-disconnected":
    case "service-error":
    case "streaming-error":
      return "#EF4444"; // Red - error state
    default:
      return "#6B7280"; // Gray - unknown/default
  }
};

const StreamControlPanel = ({
  startStream,
  toggleStream,
  startRecording,
  toggleRecording,
  accessToken,
  updateModalState,
  isStreamStarting,
  loading,
}: {
  startStream: boolean;
  toggleStream: any;
  startRecording: boolean;
  toggleRecording: () => Promise<void>;
  accessToken: string;
  isStreamStarting: boolean;
  loading: boolean;
  updateModalState: any;
}) => {
  const {
    nativeAppConnectionState,
    nativeAppStatus,
    isNativeAppConnected,
    canStartStreaming,
    canStopStreaming,
    canStartRecording,
    canStopRecording,
    shouldShowReconnect,
    clearError,
    recordingFile,
    streamStarted: currentStreamStarted,
    smartReconnect,
    connectWithDeeplink,
  } = useWsProvider();
  const [stopStreaming, setStopStreaming] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  const handleStopStream = useCallback(async () => {
    console.log("handleStopStream: starting");
    setIsStopping(true);
    try {
      if (startStream) {
        console.log("handleStopStream: calling toggleStream");
        const success = await toggleStream();
        console.log("handleStopStream: toggleStream returned", success);
        if (!success && currentStreamStarted) {
          console.error("Failed to stop stream via toggleStream");
          setStopStreaming(false);
          setIsStopping(false);
          return;
        }
      }
    } catch (error) {
      console.error("Error stopping stream:", error);
      setStopStreaming(false);
      setIsStopping(false);
    } finally {
      setIsStopping(false);
      console.log("handleStopStream: finally, isStopping set to false");
    }
  }, [
    startStream,
    toggleStream,
    accessToken,
    clearError,
    updateModalState,
    currentStreamStarted,
  ]);

  const handleStartStream = useCallback(async () => {
    if (!canStartStreaming) {
      console.warn(
        "Cannot start stream: Invalid state for streaming operation"
      );
      return;
    }
    try {
      await toggleStream();
    } catch (error) {
      console.error("Error updating stream status:", error);
    }
  }, [canStartStreaming, toggleStream, accessToken, clearError]);

  const handleStreamToggle = useCallback(async () => {
    if (!isNativeAppConnected) {
      console.warn("Cannot toggle stream: Native app not connected");
      return;
    }

    if (!canStartStreaming && !canStopStreaming) {
      console.warn(
        "Cannot toggle stream: Invalid state for streaming operation"
      );
      return;
    }

    if (startStream) {
      setStopStreaming(true);
      handleStopStream()
    } else {
      await handleStartStream();
    }
  }, [
    isNativeAppConnected,
    canStartStreaming,
    canStopStreaming,
    startStream,
    handleStartStream,
  ]);

  const handleRecordingToggle = useCallback(async () => {
    if (!isNativeAppConnected) {
      console.warn("Cannot toggle recording: Native app not connected");
      return;
    }

    if (!canStartRecording && !canStopRecording) {
      console.warn(
        "Cannot toggle recording: Invalid state for recording operation"
      );
      return;
    }

    try {
      await toggleRecording();
      clearError();
    } catch (error) {
      console.error("Error toggling recording:", error);
    }
  }, [
    isNativeAppConnected,
    canStartRecording,
    canStopRecording,
    toggleRecording,
    startRecording,
    accessToken,
    clearError,
  ]);

  return (
    <View
      style={{
        backgroundColor: AppColors.grayColor_282828,
        padding: 12,
        borderRadius: 8,
        width: "25%",
      }}
    >
      <Text style={{ color: "#FFF", fontSize: 16, marginBottom: 6 }}>
        Controls
      </Text>

      {startStream && (
        <View
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: COLORS.liveBg,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 12,
            flexDirection: "row",
            alignItems: "center",
            borderColor: COLORS.liveRed,
            borderWidth: 1,
          }}
        >
          <Image
            source={AppImages.Live_Stream}
            style={{ height: 12, width: 12, marginRight: 6 }}
            resizeMode="contain"
          />
          <Text style={{ color: COLORS.liveText, fontSize: 12 }}>LIVE</Text>
        </View>
      )}

      {/* Detailed Native App Status Box */}
      {isNativeAppConnected && (
        <View
          style={{
            backgroundColor: "rgba(40, 40, 40, 0.6)",
            padding: 6,
            borderRadius: 4,
            marginBottom: 8,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Status Row - Compact Layout */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 3,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: 9,
                  fontWeight: "600",
                  marginRight: 3,
                }}
              >
                Stream:
              </Text>
              <Text
                style={{
                  color: nativeAppStatus?.isStreaming ? "#22C55E" : "#6B7280",
                  fontSize: 9,
                }}
              >
                {nativeAppStatus?.isStreaming ? "🔴 Live" : "⚫ Off"}
              </Text>
            </View>

            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: 9,
                  fontWeight: "600",
                  marginRight: 3,
                }}
              >
                Record:
              </Text>
              <Text
                style={{
                  color: nativeAppStatus?.isRecording ? "#EF4444" : "#6B7280",
                  fontSize: 9,
                }}
              >
                {nativeAppStatus?.isRecording ? "🟢 On" : "⚫ Off"}
              </Text>
            </View>
          </View>

          {/* Service State - Compact */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                color: getStatusColor(nativeAppStatus?.state),
                fontSize: 9,
                fontWeight: "500",
              }}
            >
              {getStatusDisplayText(nativeAppStatus?.state)}
            </Text>
          </View>

          {/* Recording File Path - Only show filename */}
          {recordingFile && (
            <View style={{ marginTop: 2 }}>
              <Text
                style={{ color: "#9CA3AF", fontSize: 8, textAlign: "center" }}
                numberOfLines={1}
              >
                📁 {recordingFile.split("/").pop() || recordingFile}
              </Text>
            </View>
          )}
        </View>
      )}
      <CommonButton
        title={
          startStream
            ? "Stop Stream"
            : isStreamStarting
            ? "Starting..."
            : "Start Stream"
        }
        onTap={handleStreamToggle}
        buttonStyle={{
          backgroundColor: startStream
            ? COLORS.blue
            : AppColors.buttonRed_DC2626,
          borderWidth: 2,
          borderColor: startStream
            ? COLORS.blue
            : AppColors.buttonRedDark_991B1B,
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 8,
          marginBottom: 6,
          marginTop: 8,
          shadowColor: "transparent",
          shadowOpacity: 0,
          elevation: 0,
        }}
        disabledCustomStyle={{
          backgroundColor: startStream
            ? COLORS.blue
            : AppColors.buttonRedDark_991B1B,
          opacity: 0.7,
        }}
        image={true}
        imageUrl={startStream ? AppImages.Stop_Stream : AppImages.Start_stream}
        imageStyle={{
          width: 20,
          height: 20,
          marginRight: 8,
          resizeMode: "contain",
        }}
        imagePosition="pre"
      />
      <CommonButton
        title={startRecording ? "Stop Recording" : "Start Recording"}
        onTap={handleRecordingToggle}
        disabled={
          (!canStartRecording && !canStopRecording) ||
          isStreamStarting ||
          loading
        }
        buttonStyle={{
          backgroundColor: "#000",
          borderWidth: 2,
          borderColor: startRecording ? COLORS.redBorder : "#FFF",
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 8,
          marginBottom: 6,
          shadowColor: "transparent",
          shadowOpacity: 0,
          elevation: 0,
          marginTop: 2,
        }}
        disabledCustomStyle={{
          backgroundColor: "#000",
          opacity: 0.7,
        }}
        image={true}
        imageUrl={
          startRecording ? AppImages.Stop_Recording : AppImages.Record_Stream
        }
        imageStyle={{
          width: 20,
          height: 20,
          marginRight: 8,
          resizeMode: "contain",
        }}
        imagePosition="pre"
      />

      {startStream && (
        <View
          style={{
            backgroundColor: COLORS.obsBg,
            padding: 8,
            borderRadius: 8,
            borderWidth: 2,
            borderColor: COLORS.obsText,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Image
            source={AppImages.Obs_Icon}
            style={{ height: 24, width: 24, marginRight: 6 }}
            resizeMode="contain"
          />
          <View style={{ flexDirection: "column" }}>
            <Text style={{ color: COLORS.obsText }}>KO Broadcaster</Text>
            <Text style={{ color: COLORS.obsText }}>
              {nativeAppConnectionState === "native-app-connected"
                ? `Connected - ${nativeAppStatus?.state || "idle"}`
                : nativeAppConnectionState === "native-app-connecting"
                ? "Connecting..."
                : "Disconnected"}
            </Text>
          </View>
        </View>
      )}
      {shouldShowReconnect && (
        <CommonButton
          title="🔄 Smart Reconnect"
          onTap={smartReconnect}
          buttonStyle={{
            backgroundColor: COLORS.redBorder,
            borderWidth: 2,
            borderColor: COLORS.redBorder,
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            marginTop: 8,
            shadowColor: "transparent",
            shadowOpacity: 0,
            elevation: 0,
          }}
          textStyle={{ color: "#FFF" }}
        />
      )}
      {!isNativeAppConnected && (
        <CommonButton
          title="Connect to KO Broadcaster"
          onTap={() => {
            // Use deeplink to open KO Broadcaster if not running
            connectWithDeeplink().catch(() => {
              // If it fails, show error message
            });
          }}
          buttonStyle={{
            backgroundColor: COLORS.yellow,
            borderWidth: 2,
            borderColor: COLORS.yellow,
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            marginTop: 8,
            shadowColor: "transparent",
            shadowOpacity: 0,
            elevation: 0,
          }}
          textStyle={{ color: "#000" }}
        />
      )}
    </View>
  );
};

export default StreamControlPanel;
